import * as React from "react";

import { getMeasuredWidth } from "./measurement";
import type { RenderedTab, TabRenderMode } from "./context";

/**
 * Order the rendered tabs by their MARKERS, not by the tabs themselves.
 *
 * Each Tab leaves a hidden marker span in its original position and renders its real content
 * through a portal. Once the list starts moving hosts between slots, the tab's own element no
 * longer says anything about authoring order — the marker is the only thing that stays put.
 */
function sortRenderedTabs(tabs: RenderedTab[]): RenderedTab[] {
  return [...tabs].sort((a, b) => {
    if (a.marker === b.marker || !a.marker || !b.marker) return 0;
    const position = a.marker.compareDocumentPosition(b.marker);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/**
 * Tracks every rendered tab and decides when measurement is trustworthy enough to switch from
 * `inline` rendering to `portal` rendering.
 *
 * ── THE GATE IS ALL-OR-NOTHING, ON PURPOSE ────────────────────────────────────────────────
 *
 * The switch happens only when EVERY registered tab has a host element AND a width above zero,
 * and the overflow layout has reported itself ready. Switching early is the failure that shows:
 * the list computes a layout from partial widths, moves hosts into slots, and the user sees tabs
 * jump or briefly vanish. Waiting costs one extra frame and nothing else.
 */
export function useRenderedTabsRegistry() {
  const [renderedTabMap, setRenderedTabMap] = React.useState(() => new Map<string, RenderedTab>());
  const [renderMode, setRenderMode] = React.useState<TabRenderMode>("inline");
  const [bootstrapTabs, setBootstrapTabs] = React.useState(() => new Set<string>());
  const [readyBootstrapTabs, setReadyBootstrapTabs] = React.useState(() => new Set<string>());
  const [bootstrapOverflowReady, setBootstrapOverflowReady] = React.useState(false);

  const registerBootstrapTab = React.useCallback((value: string) => {
    setBootstrapTabs((tabs) => {
      if (tabs.has(value)) return tabs;
      const next = new Set(tabs);
      next.add(value);
      return next;
    });
    return () => {
      setBootstrapTabs((tabs) => {
        if (!tabs.has(value)) return tabs;
        const next = new Set(tabs);
        next.delete(value);
        return next;
      });
      setReadyBootstrapTabs((tabs) => {
        if (!tabs.has(value)) return tabs;
        const next = new Set(tabs);
        next.delete(value);
        return next;
      });
    };
  }, []);

  const setBootstrapTabReady = React.useCallback((value: string, ready: boolean) => {
    setReadyBootstrapTabs((tabs) => {
      if (ready === tabs.has(value)) return tabs;
      const next = new Set(tabs);
      if (ready) next.add(value);
      else next.delete(value);
      return next;
    });
  }, []);

  React.useLayoutEffect(() => {
    if (renderMode === "portal" || bootstrapTabs.size < 1) return;
    if (!bootstrapOverflowReady) return;
    for (const value of bootstrapTabs) {
      if (!readyBootstrapTabs.has(value)) return;
      const tab = renderedTabMap.get(value);
      // A zero width means this tab has not been laid out yet. One unmeasured tab is enough to
      // make the whole layout wrong, so the gate waits rather than guessing.
      if (!tab || getMeasuredWidth(tab.root) <= 0) return;
    }
    setRenderMode("portal");
  }, [bootstrapOverflowReady, bootstrapTabs, readyBootstrapTabs, renderMode, renderedTabMap]);

  const registerRenderedTab = React.useCallback((tab: RenderedTab) => {
    setRenderedTabMap((map) => {
      const existing = map.get(tab.value);
      /*
       * `import.meta.env.DEV`, NOT `process.env.NODE_ENV`. Vite does not substitute the latter
       * in application source, so it reads as production in the browser and the whole branch is
       * dead — MEASURED: the vendor emitted this warning twice for a duplicate value and this
       * port emitted it zero times, purely because of the guard. It is the repo's convention
       * everywhere else (`ErrorBoundary`, `wizard-stepper`, `instrument`) for exactly this reason.
       *
       * Two tabs with one value silently overwrite each other in this map, and the second is
       * then unreachable by keyboard with nothing on screen to suggest why.
       */
      if (import.meta.env.DEV && existing && existing.id !== tab.id) {
        console.warn(
          `Tabs received duplicate tab value "${tab.value}". Tab values must be unique within a Tabs instance.`,
        );
      }
      if (existing === tab) return map;
      const next = new Map(map);
      next.set(tab.value, tab);
      return next;
    });
    return () => {
      setRenderedTabMap((map) => {
        const existing = map.get(tab.value);
        // Guarded by id: during a swap the replacement has already registered under this value,
        // and the outgoing tab's cleanup would otherwise delete the newcomer.
        if (!existing || existing.id !== tab.id) return map;
        const next = new Map(map);
        next.delete(tab.value);
        return next;
      });
    };
  }, []);

  const updateRenderedTab = React.useCallback((value: string, updates: Partial<RenderedTab>) => {
    setRenderedTabMap((map) => {
      const existing = map.get(value);
      if (!existing) return map;
      let changed = false;
      const next = { ...existing };
      for (const [key, update] of Object.entries(updates)) {
        if ((next as Record<string, unknown>)[key] !== update) {
          changed = true;
          (next as Record<string, unknown>)[key] = update;
        }
      }
      if (!changed) return map;
      const nextMap = new Map(map);
      nextMap.set(value, next);
      return nextMap;
    });
  }, []);

  const getRenderedTab = React.useCallback(
    (value: string) => renderedTabMap.get(value),
    [renderedTabMap],
  );

  const renderedTabs = React.useMemo(
    () => sortRenderedTabs(Array.from(renderedTabMap.values())),
    [renderedTabMap],
  );

  const renderedTabOrderMap = React.useMemo(
    () => new Map(renderedTabs.map((tab, index) => [tab.value, index])),
    [renderedTabs],
  );

  const getRenderedTabOrder = React.useCallback(
    (value: string) => renderedTabOrderMap.get(value) ?? -1,
    [renderedTabOrderMap],
  );

  return {
    getRenderedTab,
    getRenderedTabOrder,
    registerBootstrapTab,
    registerRenderedTab,
    renderMode,
    renderedTabs,
    setBootstrapOverflowReady,
    setBootstrapTabReady,
    updateRenderedTab,
  };
}
